import { ReactNode, createContext, useEffect, useMemo, useState, type JSX } from 'react'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { PublicDomainDto } from '@model/PublicDomainDto'

import { useGuardedContext } from '@global/useGuardedContext'

import { useEntitySettings } from './EntityContext'

interface PublicDomainsReadContextProps {
  domains?: PublicDomainDto
}

interface PublicDomainsWriteContextProps {
  refreshDomains: () => void
}

const PublicDomainsReadContext = createContext<PublicDomainsReadContextProps | undefined>(undefined)
PublicDomainsReadContext.displayName = 'PublicDomainsReadContext'

const PublicDomainsWriteContext = createContext<PublicDomainsWriteContextProps | undefined>(
  undefined
)

export function usePublicDomainsReadContext(): PublicDomainsReadContextProps {
  return useGuardedContext(PublicDomainsReadContext)
}

export function usePublicDomainsWriteContext(): PublicDomainsWriteContextProps {
  return useGuardedContext(PublicDomainsWriteContext)
}

interface Props {
  publicDomains?: PublicDomainDto
  children: ReactNode
}

export function PublicDomainsContextProvider({ publicDomains, children }: Props): JSX.Element {
  const [entitySettings] = useEntitySettings()
  const [domains, setDomains] = useState(publicDomains)
  const apiClient = useApiClient(ClientApiClient)

  const locationHost =
    process.env.NODE_ENV === 'development'
      ? 'dev-bo01-ca-02-my.d.tickmill.com'
      : window.location.host

  useEffect(() => {
    const fetchDomains = async () => {
      try {
        const fetchedDomains = await apiClient.getPublicDomainByTickmillCompany(
          entitySettings.entity,
          locationHost
        )
        setDomains(fetchedDomains)
      } catch (error: unknown) {
        console.error('Error fetching domains:', error)
      }
    }
    fetchDomains()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationHost, entitySettings.entity])

  const writeContext = useMemo<PublicDomainsWriteContextProps>(() => {
    return {
      async refreshDomains(pubDomains?: PublicDomainDto) {
        if (!pubDomains) {
          const fetchedDomains = await apiClient.getPublicDomainByTickmillCompany(
            entitySettings.entity,
            locationHost
          )
          setDomains(fetchedDomains)
        } else {
          setDomains(domains)
        }
      },
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const readContext = useMemo<PublicDomainsReadContextProps>(() => {
    return { domains: domains }
  }, [domains])

  return (
    <PublicDomainsReadContext.Provider value={readContext}>
      <PublicDomainsWriteContext.Provider value={writeContext}>
        {children}
      </PublicDomainsWriteContext.Provider>
    </PublicDomainsReadContext.Provider>
  )
}
